import React from 'react'
import {AddressErrors, ShippingAddressInputs, InputMode} from './ShippingAddressInputs'
import {SearchPostCode} from '../../../../../Common/SearchPostCode'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {MBContentSubTitleTypography} from '../../../Atoms/Typographies'
import {MBRequiredBadge} from '../../../Atoms/Badges'
import {ErrorDisplay} from '../../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {MBMemberInput} from '../../../Atoms/MBMemberInput'
import {FlexContainer} from '../../../Atoms/Containers'
import {ShippingAddress} from '../../../../DTO/MemberShippingAddressDTO'
import {MemberNameInputs} from '../MemberNameInputs'
import {convertOnBlur} from '../../../../Utils/ConvertOnBlur'
import {useTranslation} from 'react-i18next'

export type ShippingAddressErrors = AddressErrors & {
  familyNameErrors: string[],
  firstNameErrors: string[],
  familyNameKanaErrors: string[],
  firstNameKanaErrors: string[],
  phoneErrors: string[]
}

type ShippingAddressInputContentsProps = {
  activity: InputMode
  searchPostCode: SearchPostCode
  shippingAddress: ShippingAddress
  shippingAddressErrors: ShippingAddressErrors
  onChange: (shippingAddress: ShippingAddress) => void
}

export const ShippingAddressInputContents: React.VFC<ShippingAddressInputContentsProps> = (
  {
    activity,
    searchPostCode,
    shippingAddress,
    shippingAddressErrors,
    onChange,
  },
) => {
  const {t} = useTranslation('member')

  const updateAddress = (
    name: string,
    value: string,
    shippingAddress: ShippingAddress,
  ): ShippingAddress => ({
    ...shippingAddress,
    [name]: value,
  })

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    shippingAddress: ShippingAddress,
  ): ShippingAddress => {
    const {
      name,
      value,
    } = e.target
    return updateAddress(name, value, shippingAddress)
  }

  const handleBlurInput = (
    e: React.FocusEvent<HTMLInputElement>,
    shippingAddress: ShippingAddress,
  ): ShippingAddress => {
    const {name, value} = e.target
    return updateAddress(name, convertOnBlur(name, value), shippingAddress)
  }

  const {
    familyName, firstName,
    familyNameKana, firstNameKana,
    phone,
  } = shippingAddress
  const {
    familyNameErrors, firstNameErrors,
    familyNameKanaErrors, firstNameKanaErrors,
    phoneErrors,
  } = shippingAddressErrors

  return (
    <PIMStack
      spacing={'18px'}
      divider={<MBContentBodyDivider/>}
      showEndDivider
      testId={'block-shipping-address-form'}
    >
      <MemberNameInputs
        memberName={{familyName, firstName, familyNameKana, firstNameKana}}
        memberNameErrors={
          {familyNameErrors, firstNameErrors, familyNameKanaErrors, firstNameKanaErrors}
        }
        onMemberNameChange={(memberName): void =>
          onChange({
            ...shippingAddress,
            ...memberName,
          })
        }
      />

      <ShippingAddressInputs
        activity={activity}
        searchPostCode={searchPostCode}
        address={shippingAddress}
        addressErrors={shippingAddressErrors}
        onChange={(address): void =>
          onChange({
            ...address,
            familyName: familyName,
            firstName: firstName,
            familyNameKana: familyNameKana,
            firstNameKana: firstNameKana,
            phone: phone,
          })
        }
      />

      <div data-testid={'form-phone'}>
        <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
          <label htmlFor="phone-input">
            <MBContentSubTitleTypography>電話番号</MBContentSubTitleTypography>
          </label>
          <MBRequiredBadge/>
        </PIMStack>
        <Spacer mt={'10px'} mb={'12px'}>
          <FlexContainer alignItems={'center'} justifyContent={'flex-start'} gap={'9px'}>
            <MBMemberInput
              type="tel"
              name="phone"
              autoComplete="tel"
              value={phone}
              className="form-text_01 w_100"
              id="phone-input"
              placeholder={t('入力フォームプレースホルダ.電話番号.携帯')}
              hasError={
                phoneErrors.length > 0
              }
              onChange={(e): void => {
                onChange(
                  handleChangeInput(e, shippingAddress),
                )
              }}
              onBlur={(e): void => {
                onChange(
                  handleBlurInput(e, shippingAddress),
                )
              }}
            />
          </FlexContainer>
          <ErrorDisplay errors={phoneErrors}/>
        </Spacer>
      </div>
    </PIMStack>
  )
}
